import { RunStatusBadge } from './-runStatus';
import { StartRunButtons } from './-startRunButtons';
import { useReportOnce } from './-useReportOnce';
import billingApi from '@api/billingApi';
import orgApi from '@api/orgApi';
import DefaultPage from '@components/defaultPage';
import { LoadingIndicator } from '@components/loadingIndicator';
import { RequireClaim } from '@components/requireClaim';
import useAuth from '@hooks/useAuth';
import { useTranslator } from '@i18n';
import { Text } from '@mantine/core';
import type { BillingRun } from '@models/billingRun';
import { createFileRoute, Link, Navigate, useNavigate } from '@tanstack/react-router';
import { useHandleError } from '@util/errors';
import { Table, textFilter, type Column } from 'schummar-table';

export const Route = createFileRoute('/profile/billing/runs/')({
  component: () => (
    <RequireClaim claim="orgs/billing" scope="global" fallback={<Navigate to="/profile" replace />}>
      <RunsPage />
    </RequireClaim>
  ),
});

function RunsPage() {
  const t = useTranslator();
  const navigate = useNavigate();
  const { loaded, hasClaim } = useAuth();
  const [runs, runsError] = billingApi.runs.useCache({
    disabled: !loaded || !hasClaim('orgs/billing', 'global'),
    updateOnMount: true,
  });
  const [orgs, orgsError] = orgApi.all.useCache();
  useReportOnce(runsError, !!runs);
  useHandleError(orgsError);

  const orgNames = new Map(orgs?.map((org) => [org.id, org.name]));

  const columns: Column<BillingRun, any>[] = [
    {
      id: 'month',
      header: t('billingRuns.month'),
      value: (run) => run.month,
      renderCell: (month, run) => (
        <Link to="/profile/billing/runs/$id" params={{ id: run.id }} onClick={(e) => e.stopPropagation()}>
          {month}
        </Link>
      ),
      width: 'max-content',
      filter: textFilter(),
    },
    {
      id: 'scope',
      header: t('billingRuns.scope'),
      value: (run) => (run.orgId ? (orgNames.get(run.orgId) ?? run.orgId) : t('billingRuns.allOrganizations')),
      width: 'minmax(14ch, 1fr)',
      filter: textFilter(),
    },
    {
      id: 'trigger',
      header: t('billingRuns.trigger'),
      value: (run) =>
        run.trigger === 'manual'
          ? [t('billingRuns.triggers.manual'), run.requestedBy?.name].filter(Boolean).join(': ')
          : t.unknown(`billingRuns.triggers.${run.trigger}`, undefined, { fallback: run.trigger }),
      width: 'minmax(max-content, 1fr)',
      displaySize: 'desktop',
    },
    {
      id: 'status',
      header: t('billingRuns.statusLabel'),
      value: (run) => run.status,
      renderCell: (status) => <RunStatusBadge status={status} />,
      width: 'max-content',
    },
    {
      id: 'startedAt',
      header: t('billingRuns.startedAt'),
      value: (run) => run.startedAt,
      renderCell: (value) => t.dateTimeFormat(new Date(value)),
      width: 'max-content',
      displaySize: 'desktop',
    },
    {
      id: 'finishedAt',
      header: t('billingRuns.finishedAt'),
      value: (run) => run.finishedAt ?? '',
      renderCell: (value) => (value ? t.dateTimeFormat(new Date(value)) : '-'),
      width: 'max-content',
      displaySize: 'desktop',
    },
  ];

  return (
    <DefaultPage
      title={t('billingRuns.title')}
      backButton={{ to: '/profile/billing' }}
      css={{ display: 'flex', flexDirection: 'column', gap: 'var(--mantine-spacing-md)', paddingBottom: 50 }}
    >
      <Text size="sm" c="dimmed">
        {t('billingRuns.description')}
      </Text>

      <StartRunButtons />

      {!runs ? (
        runsError ? null : (
          <LoadingIndicator />
        )
      ) : runs.items.length === 0 ? (
        <Text c="dimmed">{t('billingRuns.empty')}</Text>
      ) : (
        <Table
          items={runs.items}
          id="id"
          columns={columns}
          fullWidth
          enableSelection={false}
          enableColumnSelection={false}
          enableExport={false}
          defaultSort={[{ columnId: 'startedAt', direction: 'desc' }]}
          displaySize={{ mobile: 700, desktop: Infinity }}
          stickyHeader={{ top: 'var(--app-shell-header-height)' }}
          wrapRow={(props, run) => (
            <div
              {...props}
              data-testid="billing-run-row"
              css={{ cursor: 'pointer' }}
              onClick={() => void navigate({ to: '/profile/billing/runs/$id', params: { id: run.id } })}
            />
          )}
        />
      )}
      {runs?.hasMore && (
        <Text size="sm" c="dimmed">
          {t('billingRuns.hasMore')}
        </Text>
      )}
    </DefaultPage>
  );
}
